import { ZuiBaseElement } from '@zywave/zui-base';
import { html } from 'lit';
import { property } from 'lit/decorators.js';
import { style } from './zui-radio-group-css.js';
import type { ZuiRadio } from './zui-radio';

/**
 * `<zui-radio-group>` is a wrapper for `<zui-radio>` elements to ensure only one is chosen at a time.
 * @element zui-radio-group
 *
 * @slot - Default, unnamed slot; for inserting `<zui-radio>` elements into `<zui-radio-group>`
 *
 * @event {CustomEvent} change - Event is fired when a `<zui-radio>` is `selected`; contains details about the `value`
 */
export class ZuiRadioGroup extends ZuiBaseElement {
  /**
   * The selected value associated with a corresponding `<zui-radio>'`s `value`
   */
  @property({ type: String, reflect: true })
  selected: string | undefined = undefined;

  /**
   * The selected value associated with a corresponding `<zui-radio>'`s `value`
   */
  @property({ type: String, reflect: true })
  value: string | undefined = undefined;

  /**
   * Radio group label for accessibility purposes only
   */
  @property({ type: String })
  label: string | undefined = undefined;

  static get styles() {
    return [super.styles, style];
  }

  connectedCallback() {
    super.connectedCallback();
    this.setAttribute('role', 'radiogroup');
    if (this.label) {
      this.setAttribute('aria-label', this.label);
    }

    this.addEventListener('selected', (e: CustomEvent) => {
      this.#setSelected(e.detail);
      e.stopPropagation();
    });

    this.#handleKeyboard();
  }

  firstUpdated() {
    const defaultChecked = this.querySelector('zui-radio[checked]');
    if (defaultChecked) {
      const idValue = defaultChecked.getAttribute('identifier');
      this.#setSelected(idValue);
    } else {
      if (this.selected) {
        const selected: ZuiRadio = this.querySelector('zui-radio[identifier="' + this.selected + '"]');
        if (selected && (selected as ZuiRadio).identifier) {
          this.#selectRadio(selected);
        }
      } else {
        const firstRadio = this.querySelector('zui-radio:not([disabled])');
        if (firstRadio) {
          firstRadio.setAttribute('tabindex', '0');
        }
      }
    }
  }

  #handleKeyboard() {
    const validKeyCodes = ['ArrowLeft', 'ArrowUp', 'ArrowRight', 'ArrowDown'];
    this.addEventListener('keydown', (e) => {
      if (validKeyCodes.includes(e.key)) {
        const selectableRadios = [].slice.call(this.querySelectorAll('zui-radio:not([disabled])'));
        const activeRadio = this.querySelector('zui-radio[checked]') || this.querySelector('zui-radio[tabindex]');
        let foundIndex = 0;
        let foundRadio: ZuiRadio | null;
        if (activeRadio) {
          foundIndex = selectableRadios.findIndex(function (radio: ZuiRadio) {
            return radio.getAttribute('identifier') === activeRadio.getAttribute('identifier');
          });

          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            const nextIndex = foundIndex + 1;
            foundIndex = nextIndex === selectableRadios.length ? 0 : nextIndex;
          } else {
            const previousIndex = foundIndex - 1;
            foundIndex = previousIndex < 0 ? selectableRadios.length - 1 : previousIndex;
          }

          foundRadio = selectableRadios[foundIndex];
          if (foundRadio) {
            if (activeRadio.getAttribute('identifier') !== foundRadio.getAttribute('identifier')) {
              this.#selectRadio(foundRadio);
              this.#setSelected(foundRadio.getAttribute('identifier'));
            }
          }
        }
        e.preventDefault();
        e.stopPropagation();
      }
    });
  }

  #selectRadio(radio: ZuiRadio) {
    radio.updateComplete.then(() => {
      radio.setAttribute('checked', '');
      radio.setAttribute('tabindex', '0');
      radio.focus();
    });
  }

  #setSelected(identifier: string) {
    this.#ensureOnlyOneSelected(identifier);
    this.value = identifier;
    this.dispatchEvent(new CustomEvent('change', { detail: identifier, bubbles: true }));
    this.selected = identifier;
  }

  #ensureOnlyOneSelected(identifier: string) {
    const items = this.querySelectorAll('zui-radio[checked]');
    items.forEach((item: ZuiRadio) => {
      if (item.getAttribute('identifier') !== identifier) {
        item.removeAttribute('checked');
        item.removeAttribute('tabindex');
        item.blur();
      }
    });
  }

  render() {
    return html` <slot></slot> `;
  }
}

window.customElements.define('zui-radio-group', ZuiRadioGroup);

declare global {
  interface HTMLElementTagNameMap {
    'zui-radio-group': ZuiRadioGroup;
  }
}
